import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AddressTypeAttributes, CompanyAttributes } from '@albatrosdeveloper/ave-models-package';
import { AddressTypeService } from './address-type.service.service';
import { ICreateAddressType } from './interfaces/create-address-type.interface';
import { IUpdateAddressType } from './interfaces/update-address-type.interface';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavContainer } from '@angular/material/sidenav';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTableModule, MatRowDef } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CustomMatPaginatorIntl } from 'app/mock-api/common/customPaginatorIntl';
import { getCode } from "app/utils/method";
import { Subscription } from 'rxjs';
import { SearchService } from 'app/layout/common/search-page/search-page.service';
import { CompanyService } from '../../configuration/company/company.service.service';
import { StatusFiltersComponent } from '../../filters/status-filters/status-filters.component';
import { FilterComponent } from 'app/common/components/filter/filter.component';
import { FilterSectionComponent } from 'app/common/components/filter-section/filter-section.component';
import { ConfigurationFiltersComponent } from '../../filters/configuration-filters/configuration-filters.component';
import { IRequestResult } from 'app/common/interfaces';
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";


@Component({
  selector: "app-address-type",
  templateUrl: "./address-type.component.html",
  styleUrls: ["./address-type.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatDialogModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSortModule,
    MatPaginatorModule,
    MatIcon,
    MatSlideToggle,
    MatSelectModule,
    MatOptionModule,
    MatTooltipModule,
    MatExpansionModule,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    MatRowDef,
    MatSidenavContainer,
    FormsModule,
    StatusFiltersComponent,
    FilterComponent,
    FilterSectionComponent,
    ConfigurationFiltersComponent,
    HoverMenuDirective
  ],
  providers: [{ provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl }],
})
export class AddressTypeComponent implements AfterViewInit, OnDestroy {
  getCode = getCode;

  displayedColumns: string[] = [
    "nombre",
    "tipo",
    // "company",
    "isDelivery",
    "estado",
    "acciones",
  ];
  currentCreateAddressType: ICreateAddressType =
    this.initializeCreateAddressType();
  currentUpdateAddressType: AddressTypeAttributes | undefined;

  dataSource: AddressTypeAttributes[] = [];
  searchQuery: string = "";
  totalItems: number = 0;
  pageSize: number = 50;
  pageIndex: number = 0;

  companyList: CompanyAttributes[] = [];
  companyFilterList: CompanyAttributes[] = [];

  dialogRefAdd: MatDialogRef<any>;
  dialogRefEdit: MatDialogRef<any>;

  @ViewChild("editModal") editModal!: TemplateRef<any>;
  @ViewChild("addModal") addModal!: TemplateRef<any>;
  @ViewChild("deleteDialog") deleteDialog!: TemplateRef<any>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild("searchInput") searchInput!: ElementRef;

  //BUSCADOR
  searchSubscription: Subscription;

  // Status Filter Properties
  selectedStatuses: number[] = [1, 0]; // Default to show both active and inactive

  // Company Filter Properties
  form: FormGroup;

  constructor(
    private readonly addressTypeService: AddressTypeService,
    private readonly companyService: CompanyService,
    private readonly searchService: SearchService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder
  ) {
    // Initialize form for filters
    this.form = this.fb.group({
      companyId: [null],
    });
  }

  ngAfterViewInit(): void {
    this.onHandleSearch();
    this.setupPaginationAndSorting();
    this.findAllWithCount();
  }

  private initializeCreateAddressType(): ICreateAddressType {
    return { name: "", type: "" };
  }

  private setupPaginationAndSorting(): void {
    this.paginator.page.subscribe(() => this.findAllWithCount());
    this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = 0;
      this.findAllWithCount();
    });
  }

  findAllWithCount(): void {
    const pageIndex = this.paginator.pageIndex + 1;
    const sortField = this.getSortField();
    const sortDirection: "ASC" | "DESC" =
      this.sort.direction === "asc" ? "ASC" : "DESC";

    this.addressTypeService
      .findAllWithCount({
        currentPage: pageIndex,
        limitPerPage: this.pageSize,
        search: this.searchQuery,
        sort: sortField,
        sortDirection: sortDirection,
        // Add filter parameters
        status: this.selectedStatuses.length ? this.selectedStatuses : null,
        companyId: this.form.get("companyId")?.value || null,
      })
      .subscribe((data) => {
        this.dataSource = data?.data?.[0]?.data ?? [];
        this.totalItems = data?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
        this.paginator.length = this.totalItems;
      });
  }

  private getSortField(): keyof AddressTypeAttributes {
    const validSortFields: (keyof AddressTypeAttributes)[] = [
      "name",
      "type",
      "statusId",
    ];
    return validSortFields.includes(
      this.sort.active as keyof AddressTypeAttributes
    )
      ? (this.sort.active as keyof AddressTypeAttributes)
      : "name";
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.findAllWithCount();
  }

  onSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery = input.value.trim();
    if (this.searchQuery.length >= 2 || this.searchQuery.length === 0) {
      this.paginator.pageIndex = 0;
      this.findAllWithCount();
    }
  }

  public onHandleSearch() {
    this.searchSubscription = this.searchService.searchTerm$.subscribe(
      (term) => {
        this.searchQuery = term;
        if (this.searchQuery.length >= 2 || this.searchQuery.length === 0) {
          this.paginator.pageIndex = 0;
          this.findAllWithCount();
        }
      }
    );
  }

  trackByFn(index: number, item: AddressTypeAttributes): string {
    return item._id.toString();
  }

  openCreateModal(): void {
    this.currentCreateAddressType = this.initializeCreateAddressType();
    this.dialogRefAdd = this.dialog.open(this.addModal, {
      panelClass: "form-modal",
      disableClose: true,
    });
    // this.dialogRefAdd.afterClosed().subscribe((result) => result && this.createAddressType());
    this.dialogRefAdd.afterClosed().subscribe((result) => {
      result;
    });
  }

  openEditModal(addressType: AddressTypeAttributes): void {
    this.currentUpdateAddressType = { ...addressType };
    this.dialogRefEdit = this.dialog.open(this.editModal, {
      panelClass: "form-modal",
      disableClose: true,
    });
    // this.dialogRefEdit.afterClosed().subscribe((result) => result && this.updateAddressType(addressType._id.toString()));
    this.dialogRefEdit.afterClosed().subscribe((result) => {
      result;
    });
  }

  createAddressType(): void {
    this.addressTypeService.create(this.currentCreateAddressType).subscribe({
      next: () => {
        this.snackBar.open("Registro creado con éxito", "Cerrar", {
          duration: 3000,
        });
        this.findAllWithCount();
        this.dialogRefAdd.close();
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error al crear el registro",
          "Cerrar",
          { duration: 3000 }
        );
      },
    });
  }

  updateAddressType(id: string): void {
    if (this.currentUpdateAddressType) {
      const updatedAddressType: IUpdateAddressType = {
        ...this.currentUpdateAddressType,
      };

      this.addressTypeService.update(id, updatedAddressType).subscribe({
        next: () => {
          this.snackBar.open("Registro actualizado con éxito", "Cerrar", {
            duration: 3000,
          });
          this.findAllWithCount();
          this.dialogRefEdit.close();
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || "Error al actualizar el registro",
            "Cerrar",
            { duration: 3000 }
          );
        },
      });
    }
  }

  onStatusChange(event: boolean, addressType: AddressTypeAttributes): void {
    const originalStatusId = addressType.statusId;
    const updatedStatusId = event ? 1 : 0;
    const updatedAddressType = { ...addressType, statusId: updatedStatusId };

    this.addressTypeService
      .update(addressType._id.toString(), updatedAddressType)
      .subscribe({
        next: () => {
          this.findAllWithCount();
        },
        error: () => {
          addressType.statusId = originalStatusId;
          this.snackBar.open("Error al actualizar el estado.", "Cerrar", {
            duration: 3000,
          });
          this.findAllWithCount();
        },
      });
  }

    onIsDeliveryChange(event: boolean, addressType: AddressTypeAttributes): void {
    const originalStatusId = addressType.isDelivery;
    const updatedStatusId = event ? true : false;
    const updatedAddressType = { ...addressType, isDelivery: updatedStatusId };

    this.addressTypeService
      .update(addressType._id.toString(), updatedAddressType)
      .subscribe({
        next: () => {
          this.findAllWithCount();
        },
        error: () => {
          addressType.isDelivery = originalStatusId;
          this.snackBar.open("Error al actualizar el estado.", "Cerrar", {
            duration: 3000,
          });
          this.findAllWithCount();
        },
      });
  }

  openDeleteDialog(addressType: AddressTypeAttributes): void {
    this.dialog
      .open(this.deleteDialog, {
        data: addressType,
        panelClass: "delete-modal",
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result) => result && this.delete(addressType._id.toString()));
  }

  delete(id: string): void {
    this.addressTypeService.remove(id).subscribe(() => {
      this.findAllWithCount();
      this.snackBar.open("Registro eliminado con éxito", "Cerrar", {
        duration: 3000,
      });
    });
  }

  // Status Filter Methods
  applyStatusFilter(): void {
    this.paginator.pageIndex = 0; // Reset to first page when filtering
    this.findAllWithCount();
  }

  // Company Filter Methods
  onFilterEvent(event: any): void {
    this.paginator.pageIndex = 0; // Reset to first page when filtering
    this.findAllWithCount();
  }

  onCleanEvent(event: any): void {
    this.form.patchValue({
      companyId: null,
    });
    this.paginator.pageIndex = 0;
    this.findAllWithCount();
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }
}
